// src/pages/ProductPage.js
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot, runTransaction } from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { firestore, storage } from '../firebase';
import { AppContext } from '../AppContext';

import { useCart } from '../CartContext';
import { toast } from 'react-toastify';

import { getFunctions, httpsCallable } from 'firebase/functions';

const ALLOWED_EDITORS = new Set([
  'h0fEIStkM4XHTDErZM5i8XIo9rA3',
  'OHaY20b54nTGd5BBgbJOL2BHEbX2',
  'Mkf4631LCEOYmHXWRzGirPdxBrt1'
]);

const ProductPage = () => {
  const { lines = [], updateCart } = useCart();
  const { currentUser } = useContext(AppContext);

  const { id } = useParams();
  const navigate = useNavigate();

  const [products, setProducts] = useState(null); // null=loading
  const [idx, setIdx] = useState(0); // selected image index

  // edit state
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftDesc, setDraftDesc] = useState('');
  const [draftPriceUsd, setDraftPriceUsd] = useState('');
  /**
   * galleryDraft is the unified list we can reorder:
   *  - { type:'url', url: string }
   *  - { type:'file', file: File, preview: string }
   */
  const [galleryDraft, setGalleryDraft] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({}); // fileIndex -> %

  const fileInputRef = useRef(null);

  useEffect(() => {
    const ref = doc(firestore, 'streetFlavor', 'products');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const arr = snap.exists() ? snap.data()?.products || [] : [];
        setProducts(arr);
      },
      (err) => {
        console.error('Product snapshot error:', err);
        setProducts([]);
      }
    );
    return () => unsub();
  }, []);

  const product = useMemo(() => {
    if (!Array.isArray(products)) return null;
    return products.find((p) => p?.id === id) || null;
  }, [products, id]);

  useEffect(() => {
    setIdx(0); // reset gallery when product changes
  }, [product?.id]);

  const viewerId = currentUser?.userId || currentUser?.uid || null;
  const canEdit = !!viewerId && ALLOWED_EDITORS.has(viewerId);

  const fmtUSD = (cents) => {
    const n = Number(cents);
    if (!Number.isFinite(n)) return '-';
    return (n / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
  };

  const images = Array.isArray(product?.images) ? product.images : [];
  const main = images[idx] || '';

  const addToCart = React.useCallback(() => {
    if (!product) return;
    const lineId = product.id;
    const image =
      Array.isArray(product.images) && product.images.length ? product.images[0] : '';
    const existing = Array.isArray(lines)
      ? lines.find((l) => l.lineId === lineId)
      : null;

    let nextLines;
    if (existing) {
      nextLines = lines.map((l) =>
        l.lineId === lineId ? { ...l, qty: Number(l.qty || 0) + 1 } : l
      );
    } else {
      nextLines = [
        ...(Array.isArray(lines) ? lines : []),
        {
          lineId,
          title: product.name,
          priceCents: Number(product.priceCents) || 0,
          image,
          qty: 1
        }
      ];
    }

    const nextQty = nextLines.reduce((s, l) => s + Number(l.qty || 0), 0);
    updateCart?.({ qty: nextQty, lines: nextLines });

    try {
      localStorage.setItem('sf_cart_v1', JSON.stringify({ lines: nextLines }));
    } catch (_) {}

    toast.success('Added to cart');
  }, [product, lines, updateCart]);

  /* ------------------------ Edit Mode Helpers ------------------------ */

  const enterEdit = () => {
    if (!product) return;
    setDraftName(product.name || '');
    setDraftDesc(product.description || '');
    setDraftPriceUsd(
      Number(product.priceCents || 0) > 0
        ? (Number(product.priceCents) / 100).toFixed(2)
        : ''
    );
    setGalleryDraft((product.images || []).map((u) => ({ type: 'url', url: u })));
    setUploadProgress({});
    setEditing(true);
  };

  const exitEdit = () => {
    // cleanup previews for files
    galleryDraft.forEach((g) => {
      if (g.type === 'file' && g.preview) URL.revokeObjectURL(g.preview);
    });
    setEditing(false);
    setSaving(false);
    setUploadProgress({});
    setGalleryDraft([]);
  };

  const onPickFiles = (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    const newItems = list.map((f) => ({
      type: 'file',
      file: f,
      preview: URL.createObjectURL(f)
    }));
    setGalleryDraft((prev) => [...prev, ...newItems]);
    // reset input so the same file can be selected again later
    e.target.value = '';
  };

  const removeGalleryItem = (i) => {
    setGalleryDraft((prev) => {
      const arr = prev.slice();
      const item = arr[i];
      if (item?.type === 'file' && item.preview) URL.revokeObjectURL(item.preview);
      arr.splice(i, 1);
      return arr;
    });
    // also adjust selected hero index if needed
    if (i === idx) setIdx(0);
  };

  const moveItem = (i, dir) => {
    setGalleryDraft((prev) => {
      const j = i + dir;
      if (i < 0 || j < 0 || i >= prev.length || j >= prev.length) return prev;
      const arr = prev.slice();
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return arr;
    });
  };

  const priceCentsFromDraft = () => {
    const n = Number(draftPriceUsd);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.round(n * 100);
    };

  const validateDraft = () => {
    if (!draftName.trim()) return 'Please enter a product name.';
    if (!draftDesc.trim()) return 'Please enter a product description.';
    if (priceCentsFromDraft() <= 0) return 'Please enter a valid price greater than 0.';
    if (!galleryDraft.length) return 'Please include at least one image.';
    return '';
  };

  const uploadOne = (file, path, index) =>
    new Promise((resolve, reject) => {
      const sRef = storageRef(storage, path);
      const task = uploadBytesResumable(sRef, file);
      task.on(
        'state_changed',
        (snap) => {
          const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
          setUploadProgress((m) => ({ ...m, [index]: pct }));
        },
        (err) => reject(err),
        async () => {
          try {
            const url = await getDownloadURL(task.snapshot.ref);
            resolve(url);
          } catch (e) {
            reject(e);
          }
        }
      );
    });

const saveEdits = async () => {
  if (!product) return;
  const err = validateDraft();
  if (err) {
    toast.error(err);
    return;
  }
  setSaving(true);
  try {
    // 1) Upload any new images to Storage first (keeps your existing UX)
    const files = galleryDraft
      .map((g, i) => ({ g, i }))
      .filter((x) => x.g.type === 'file');

    const ts = Date.now();
    const uploaded = await Promise.all(
      files.map(({ g, i }, k) =>
        uploadOne(g.file, `streetflavor_products/${ts}_${k}_${g.file.name}`, i)
      )
    );
    const indexToUrl = new Map();
    files.forEach(({ i }, idx) => indexToUrl.set(i, uploaded[idx]));

    const finalImages = galleryDraft.map((g, i) =>
      g.type === 'url' ? g.url : indexToUrl.get(i)
    );

    const pc = priceCentsFromDraft();

    // 2) Call Cloud Function to update the product server-side
    const fn = httpsCallable(getFunctions(), 'editBizarreProduct');
    const res = await fn({
      id: product.id,
      name: draftName.trim(),
      description: draftDesc.trim(),
      priceCents: pc,
      images: finalImages
    });

    if (!res?.data?.ok) {
      throw new Error('Edit failed.');
    }

    toast.success('Product updated');
    exitEdit();
  } catch (e) {
    console.error('Save edits error:', e);
    toast.error(e?.message || 'Unable to save changes.');
    setSaving(false);
  }
};

  return (
    <div className="sf-product">
      <button className="sf-back" onClick={() => navigate('/shop')}>← Back to Shop</button>

      {products === null ? (
        <div className="sf-detail skeleton" />
      ) : !product ? (
        <p className="sf-empty">Product not found.</p>
      ) : (
        <div className="sf-detail">
          {/* Gallery */}
          <div className="sf-gallery">
            <div className="sf-hero">
              {editing ? (
                // When editing, show hero from draft array
                (() => {
                  const current = galleryDraft[idx];
                  if (!current) return <div className="sf-hero-ph" />;
                  const src = current.type === 'url' ? current.url : current.preview;
                  return <img src={src} alt={`${draftName || product.name} - ${idx + 1}`} />;
                })()
              ) : (
                <>
                  {main ? (
                    <img src={main} alt={`${product.name} - ${idx + 1}`} />
                  ) : (
                    <div className="sf-hero-ph" />
                  )}
                </>
              )}
            </div>

            {/* Thumbnails */}
            {editing ? (
              <>
                <div className="sf-thumbs">
                  {galleryDraft.map((g, i) => {
                    const src = g.type === 'url' ? g.url : g.preview;
                    return (
                      <div key={i} className={`sf-thumb edit ${i === idx ? 'active' : ''}`}>
                        <button
                          type="button"
                          className="sf-thumb-btn"
                          onClick={() => setIdx(i)}
                          aria-label={`Select image ${i + 1}`}
                          title="Select"
                        >
                          <img src={src} alt={`thumbnail ${i + 1}`} />
                        </button>
                        <div className="sf-thumb-tools">
                          <button type="button" className="tool" onClick={() => moveItem(i, -1)} aria-label="Move left">←</button>
                          <button type="button" className="tool" onClick={() => moveItem(i, +1)} aria-label="Move right">→</button>
                          <button type="button" className="tool danger" onClick={() => removeGalleryItem(i)} aria-label="Remove">✕</button>
                        </div>
                        {g.type === 'file' && saving && (
                          <div className="sf-progress">
                            <div
                              className="sf-progress-bar"
                              style={{ width: `${uploadProgress[i] || 0}%` }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="sf-file-row">
                  <button
                    type="button"
                    className="sf-btn upload"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={saving}
                  >
                    + Add Images
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={onPickFiles}
                    className="sf-file-hidden"
                    aria-hidden="true"
                    tabIndex={-1}
                  />
                </div>
              </>
            ) : (
              images.length > 1 && (
                <div className="sf-thumbs">
                  {images.map((u, i) => (
                    <button
                      key={i}
                      type="button"
                      className={`sf-thumb ${i === idx ? 'active' : ''}`}
                      onClick={() => setIdx(i)}
                      aria-label={`Show image ${i + 1}`}
                    >
                      <img src={u} alt={`${product.name} thumbnail ${i + 1}`} />
                    </button>
                  ))}
                </div>
              )
            )}
          </div>

          {/* Info */}
          <div className="sf-info">
            {!editing ? (
              <>
                <h1 className="sf-name">{product.name}</h1>
                <div className="sf-price">{fmtUSD(product.priceCents)}</div>
                <p className="sf-desc">{product.description}</p>

                <div className="sf-actions">
                  <button className="sf-btn" onClick={addToCart}>Add to Cart</button>
                  <button className="sf-btn ghost" onClick={() => navigate('/shop')}>Continue Shopping</button>
                  {canEdit && (
                    <button className="sf-btn ghost" onClick={enterEdit}>Edit</button>
                  )}
                </div>
              </>
            ) : (
              <>
                <label className="sf-label">
                  Product Name
                  <input
                    className="sf-input"
                    type="text"
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    disabled={saving}
                  />
                </label>

                <label className="sf-label">
                  Price (USD)
                  <input
                    className="sf-input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={draftPriceUsd}
                    onChange={(e) => setDraftPriceUsd(e.target.value)}
                    disabled={saving}
                  />
                </label>

                <label className="sf-label">
                  Description
                  <textarea
                    className="sf-textarea"
                    rows={6}
                    value={draftDesc}
                    onChange={(e) => setDraftDesc(e.target.value)}
                    disabled={saving}
                  />
                </label>

                <div className="sf-actions">
                  <button className="sf-btn" onClick={saveEdits} disabled={saving}>
                    {saving ? 'Saving…' : 'Save Changes'}
                  </button>
                  <button className="sf-btn ghost" onClick={exitEdit} disabled={saving}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
:root{
  --sf-gold:#fdca05;
  --sf-gold-10:rgba(128,128,78,.10);
  --sf-gold-18:rgba(128,128,78,.18);
  --sf-bd:rgba(255,255,255,.12);
  --sf-panel:#0b0b0b;
}

body {
  font-family: 'CustomFont', sans-serif;
  margin: 0;
  padding: 0;
  background-color: #000;
  color: #fff;
}

.sf-product{
  background:#000; color:#fff;
  min-height:calc(100vh - 120px);
  max-width:1200px; margin:18px auto 80px; padding:0 16px;
}

.sf-back{
  background:transparent; color:var(--sf-gold);
  border:1px solid rgba(128,128,78,.2); border-radius:10px;
  padding:.5rem .8rem; cursor:pointer; margin:6px 0 14px;
}

.sf-detail{
  display:grid;
  grid-template-columns: 1.2fr 1fr;
  gap:22px;
}
@media (max-width: 900px){
  .sf-detail{ grid-template-columns:1fr; }
}

/* gallery */
.sf-gallery{
  background:var(--sf-panel);
  border:1px solid var(--sf-bd);
  border-radius:14px;
  padding:14px;
}
.sf-hero{
  aspect-ratio: 1 / 1;
  background:#111;
  border-radius:10px;
  overflow:hidden;
}
.sf-hero img{ width:100%; height:100%; object-fit:cover; display:block; }
.sf-hero-ph{ width:100%; height:100%; background:linear-gradient(135deg,#151515,#1c1c1c); }

.sf-thumbs{
  display:flex; gap:10px; flex-wrap:wrap;
  margin-top:12px;
}
.sf-thumb{
  width:80px; height:80px; padding:0; border:0;
  border-radius:10px; overflow:hidden; cursor:pointer;
  opacity:.65; outline:none;
  border:1px solid rgba(255,255,255,.14);
  background:#0f0f0f;
  transition:opacity .12s ease, border-color .12s ease, transform .12s ease;
}
.sf-thumb:hover{ opacity:.85; transform:translateY(-1px); }
.sf-thumb.active{ opacity:1; border-color:var(--sf-gold); }
.sf-thumb img{ width:100%; height:100%; object-fit:cover; display:block; }

.sf-thumb.edit{
  position:relative;
  padding:0;
}
.sf-thumb-btn{
  width:100%; height:100%; padding:0; border:0; background:transparent; cursor:pointer;
}
.sf-thumb-tools{
  position:absolute; inset:auto 4px 4px 4px;
  display:flex; gap:6px; justify-content:space-between;
}
.sf-thumb .tool{
  background:rgba(0,0,0,.6);
  color:#fff;
  border:1px solid rgba(255,255,255,.2);
  padding:2px 6px;
  border-radius:8px; cursor:pointer; font-weight:800;
}
.sf-thumb .tool.danger{
  border-color: rgba(255,80,80,.4);
  color: #ff8080;
}

.sf-progress{
  position:absolute; left:4px; right:4px; bottom:4px;
  height:6px; background:rgba(255,255,255,.15);
  border-radius:999px; overflow:hidden;
}
.sf-progress-bar{
  height:6px; background:var(--sf-gold);
}

/* info */
.sf-info{
  background:var(--sf-panel);
  border:1px solid var(--sf-bd);
  border-radius:14px;
  padding:18px;
}
.sf-name{
  font-size:1.8rem; font-weight:900; margin-bottom:8px;
  text-shadow:0 0 14px var(--sf-gold-10);
}
.sf-price{
  color:var(--sf-gold); font-weight:900; font-size:1.2rem; margin-bottom:12px;
}
.sf-desc{
  line-height:1.55; opacity:.95; white-space:pre-wrap;
}

.sf-actions{ display:flex; gap:12px; margin-top:16px; flex-wrap:wrap; }
.sf-btn{
  padding:.75rem 1.2rem; border-radius:12px; font-weight:900; cursor:pointer;
  border:2px solid var(--sf-gold);
  background:var(--sf-gold); color:#000;
  box-shadow:0 6px 18px rgba(128,128,78,.26);
}
.sf-btn.ghost{
  background:transparent; color:var(--sf-gold); border:1px solid rgba(128,128,78,.25); box-shadow:none;
}

/* form */
.sf-label{ display:flex; flex-direction:column; gap:8px; font-weight:800; color:#fff; margin-bottom:8px; }
.sf-input, .sf-textarea{
  width:100%;
  background:rgba(255,255,255,.06);
  border:1px solid rgba(255,255,255,.18);
  color:#fff;
  border-radius:12px;
  padding:12px 14px;
  font-size:1rem;
  outline:none;
}
.sf-input::placeholder, .sf-textarea::placeholder{ color:rgba(255,255,255,.55); }
.sf-input:focus, .sf-textarea:focus{
  border-color:var(--sf-gold);
  box-shadow:0 0 0 2px var(--sf-gold-10) inset;
}

.sf-file-row{ margin-top:10px; }
.sf-file-hidden{ display:none; }
.sf-btn.upload{
  background:transparent;
  color:#000;
  position:relative;
  overflow:hidden;
  background:var(--sf-gold);
  border:1px solid var(--sf-gold);
  font-weight:900;
}

/* skeleton */
.skeleton{
  height:360px;
  background:linear-gradient(135deg,#111,#171717);
  border:1px solid var(--sf-bd);
  border-radius:14px;
  position:relative; overflow:hidden;
}
.skeleton::after{
  content:''; position:absolute; inset:0;
  background:linear-gradient(90deg,rgba(255,255,255,0) 0%,rgba(255,255,255,.07) 50%,rgba(255,255,255,0) 100%);
  animation:sh 1.2s infinite;
}
@keyframes sh{ 0%{ transform:translateX(-100%);} 100%{ transform:translateX(100%);} }

.sf-empty{ opacity:.8; margin-top:10px; }
      `}</style>

    </div>
  );
};

export default ProductPage;
